import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Avatar, Empty, Modal, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { getHistory } from './quotationService';
import {
  formatHistoryEntry,
  formatHistoryTimestamp,
  getHistoryAvatarStyle,
  renderHistoryText,
} from './historyUiUtils';

const PAGE_SIZE = 8;
const HISTORY_SORT = { sort: 'createdAt', order: 'desc' };

function HistoryModalTitle({ total, page, loading, onPageChange }) {
  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const showPager = total > PAGE_SIZE;

  return (
    <div className="Quotation_historyModalTitle">
      <span className="Quotation_historyModalTitleText">History</span>
      {showPager && (
        <div className="Quotation_historyPager">
          <span
            role="button"
            tabIndex={loading || page <= 1 ? -1 : 0}
            className={`Quotation_historyPagerArrow${loading || page <= 1 ? ' is-disabled' : ''}`}
            onClick={() => !loading && page > 1 && onPageChange(page - 1)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !loading && page > 1) {
                onPageChange(page - 1);
              }
            }}
            aria-label="Previous page"
          >
            <LeftOutlined />
            <span>Prev</span>
          </span>
          <span
            role="button"
            tabIndex={loading || page >= totalPages ? -1 : 0}
            className={`Quotation_historyPagerArrow${loading || page >= totalPages ? ' is-disabled' : ''}`}
            onClick={() => !loading && page < totalPages && onPageChange(page + 1)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !loading && page < totalPages) {
                onPageChange(page + 1);
              }
            }}
            aria-label="Next page"
          >
            <span>Next</span>
            <RightOutlined />
          </span>
        </div>
      )}
    </div>
  );
}

export default function ModalHistoryComponent({ isVisible, onClose, id }) {
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async (pageNum) => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await getHistory(id, {
        page: pageNum,
        limit: PAGE_SIZE,
        ...HISTORY_SORT,
      });
      setHistory(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistory([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isVisible) {
      setPage(1);
      return;
    }
    if (!id) return;
    fetchHistory(page);
  }, [isVisible, id, page, fetchHistory]);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  return (
    <Modal
      open={isVisible}
      title={
        <HistoryModalTitle
          total={total}
          page={page}
          loading={loading}
          onPageChange={handlePageChange}
        />
      }
      onCancel={onClose}
      footer={null}
      className="Quotes_modal Modal_ht"
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div className="Quotation_history">
          {history.length > 0 ? (
            history.map((item) => {
              const { relative, absolute } = formatHistoryTimestamp(item.createdAt);
              const { mainText, userName } = formatHistoryEntry(item);
              return (
                <div className="Quotation_subHistory" key={item._id}>
                  <Avatar size={36} style={getHistoryAvatarStyle(userName)}>
                    {userName?.charAt(0).toUpperCase() || '?'}
                  </Avatar>
                  <div className="Quotation_historyContent">
                    <p className="Quotation_historyText">{renderHistoryText(mainText)}</p>
                    <p className="Quotation_historyByline">By {userName}</p>
                  </div>
                  <div className="Quotation_subHistoryDate">
                    {relative && (
                      <span className="Quotation_historyRelative">{relative}</span>
                    )}
                    {absolute && (
                      <span className="Quotation_historyAbsolute">{absolute}</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            !loading && (
              <Empty
                className="Quotation_historyEmpty"
                description="No history yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          )}
        </div>
      </Spin>
    </Modal>
  );
}
